import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link, Routes, Route } from "react-router-dom";
import {
  Home,
  MapPinPlus,
  CalendarPlus,
  Settings,
  LogOut,
  BookOpenText,
  Lock,
} from "lucide-react";
import api from "../api";
import VenueSelection from "../pages/VenueSelection";
import SettingsPage from "../pages/SettingsPage";
import { useAuth } from "../utils/authprovider";
import { getUserSelectedVenues } from "../utils/queries";
import "../styles/SideMenu.css";

export default function SideMenu({ userId }) {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const [subscription, setSubscription] = useState(null);
  const [selectedVenueNames, setSelectedVenueNames] = useState([]);

  /* ───────── Helpers ───────── */
  const isTrial = subscription?.plan === "trial";
  const isEssentials = subscription?.plan === "essentials";
  const creditsRemaining = subscription?.credits_remaining ?? 0;
  const isEssentialsOut = isEssentials && creditsRemaining <= 0;

  // true  → user can click and proceed
  // false → button locked
  const canAddVenue = !(isTrial || isEssentialsOut);

  const promptUpgrade = useCallback(() => {
    navigate(`/couples_plans/${userId}`);
  }, [navigate, userId]);

  /* ───────── Init ───────── */
  useEffect(() => {
    (async () => {
      try {
        const [{ data: acct }, venues] = await Promise.all([
          api.get("/account_status/", { params: { user_id: userId } }),
          getUserSelectedVenues(userId),
        ]);

        if (acct?.status === "success") {
          setSubscription(acct.account_status.subscription);
        }

        if (Array.isArray(venues) && venues.length > 0) {
          // normalize to names (supports {name, date} or string)
          const names = venues.map((v) => (typeof v === "string" ? v : v.name));
          setSelectedVenueNames(names);
        } else {
          setSelectedVenueNames([]);
        }
      } catch (err) {
        console.error("SideMenu init error:", err);
      }
    })();
  }, [userId]);

  /* ───────── Actions ───────── */
  const handlePickVenue = () => {
    if (canAddVenue) {
      navigate(`/find_venues/${userId}`);
    } else {
      promptUpgrade();
    }
  };

  const handleAddDates = () => {
    // Essentials with 0 credits → never back to Preferences: funnel to upgrade
    if (isEssentialsOut) {
      promptUpgrade();
      return;
    }

    // Non-essentials (& not trial):
    if (selectedVenueNames.length > 0) {
      navigate(`/preferences/${userId}`, {
        state: { selectedVenues: selectedVenueNames },
      });
    } else {
      // No venues yet — take them to pick one first
      navigate(`/find_venues/${userId}`);
    }
  };

  const handleLogout = async (e) => {
    e.preventDefault();
    try {
      await logout();
    } finally {
      navigate("/signin");
    }
  };

  /* ───────── UI ───────── */
  const LockedIcon = () => <Lock className="locked-icon" />;

  return (
    <div className="dashboard-side-menu">
      {/* Optional nested routes */}
      <Routes>
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/venue_selection/:userId" element={<VenueSelection />} />
      </Routes>

      {/* Home */}
      <Link
        to={`/dashboard/${userId}`}
        className="dashboard-side-menu-item"
        title="Home"
        aria-label="Home"
      >
        <Home />
      </Link>

      {/* Add / pick venue */}
      <button
        type="button"
        onClick={handlePickVenue}
        className={`dashboard-side-menu-item menu-button ${
          canAddVenue ? "" : "locked"
        }`}
        title={
          canAddVenue ? "Pick another venue" : "Upgrade your plan to add venues"
        }
        aria-label={canAddVenue ? "Pick another venue" : "Upgrade your plan"}
      >
        {canAddVenue ? <MapPinPlus /> : <LockedIcon />}
      </button>

      {/* Add more dates:
          • Essentials out of credits → locked + upgrade
          • Non-essentials (not trial) with venues → enabled and goes to Preferences
          • Non-essentials (not trial) without venues → enabled but routes to Find Venues
      */}
      {!isTrial && (
        <button
          type="button"
          onClick={handleAddDates}
          className={`dashboard-side-menu-item menu-button ${
            isEssentialsOut ? "locked" : ""
          }`}
          title={
            isEssentialsOut
              ? "Upgrade your plan to add dates"
              : selectedVenueNames.length > 0
              ? "Add more dates"
              : "Pick a venue first"
          }
          aria-label={
            isEssentialsOut
              ? "Upgrade your plan"
              : selectedVenueNames.length > 0
              ? "Add more dates"
              : "Pick a venue first"
          }
        >
          {isEssentialsOut ? <LockedIcon /> : <CalendarPlus />}
        </button>
      )}

      {/* Docs */}
      <Link
        to={`/help/${userId}`}
        className="dashboard-side-menu-item"
        title="Help & Docs"
        aria-label="Help & Docs"
      >
        <BookOpenText />
      </Link>

      {/* Settings */}
      <Link
        to={`/settings/${userId}`}
        className="dashboard-side-menu-item"
        title="Settings"
        aria-label="Settings"
      >
        <Settings />
      </Link>

      {/* Logout */}
      <button
        type="button"
        onClick={handleLogout}
        className="dashboard-side-menu-item menu-button logout-button"
        title="Logout"
        aria-label="Logout"
      >
        <LogOut />
      </button>
    </div>
  );
}
